<template>
  <div class="nd-page">
    <header class="nd-page-header">
      <div>
        <h1 id="title" class="page-title">{{ pageTitle }}</h1>
        <p class="nd-page-kicker">Solicite exames e registre orientações laboratoriais.</p>
      </div>
    </header>

    <v-card class="nd-section-card">
      <v-form ref="testForm" v-model="formValid">
        <div class="nd-table-toolbar">
          <div>
            <div class="nd-table-toolbar__title">Dados do exame</div>
            <div class="nd-table-toolbar__subtitle">Vincule paciente, laboratório, médico e tipo solicitado.</div>
          </div>
        </div>

        <div class="nd-form-grid nd-form-grid--two">
          <v-select
            id="patientSelect"
            label="Paciente"
            v-model="test.patientId"
            :items="patients"
            item-text="name"
            item-value="id"
            :rules="[rules.required]"
            outlined
            dense
            no-data-text="Nenhum paciente encontrado"
           hide-details="auto"></v-select>
          <v-select
            id="laboratorySelect"
            label="Laboratório"
            v-model="test.laboratoryId"
            :items="laboratories"
            item-text="name"
            item-value="id"
            outlined
            dense
            clearable
            no-data-text="Nenhum laboratório encontrado"
           hide-details="auto"></v-select>
          <v-text-field
            id="doctorName"
            label="Médico"
            v-model="test.doctorName"
            :rules="[rules.required]"
            outlined
            dense
           hide-details="auto"></v-text-field>
          <v-select
            id="testTypeSelect"
            label="Tipo de exame"
            v-model="test.testTypeId"
            :items="testTypes"
            item-text="name"
            item-value="id"
            :rules="[rules.required]"
            outlined
            dense
            no-data-text="Nenhum tipo de exame encontrado"
           hide-details="auto"></v-select>
          <v-textarea
            id="orientation"
            class="nd-form-grid__wide"
            label="Orientação"
            v-model="test.orientation"
            outlined
            dense
            rows="3"
            auto-grow
           hide-details="auto"></v-textarea>
        </div>

        <div class="nd-form-actions">
          <v-btn id="btnCancel" type="button" color="primary" outlined elevation="0" @click="$router.push('/exames')">
            Voltar
          </v-btn>
          <v-btn id="btnSave" type="button" color="primary" elevation="0" @click="saveTest" :loading="loadingSave">
            Salvar
          </v-btn>
        </div>
      </v-form>
    </v-card>

    <v-overlay :value="loadingPage">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import Vue from "vue";
import axios from "axios";

export default {
  name: "TestsDetails",

  data: () => ({
    test: {
      id: null,
      patientId: null,
      laboratoryId: null,
      orientation: "",
      doctorName: "",
      testTypeId: null,
    },
    patients: [],
    laboratories: [],
    testTypes: [],

    loadingPage: false,
    loadingSave: false,
    formValid: false,

    rules: {
      required: (value) => !!value || "Campo obrigatório.",
    },
  }),

  computed: {
    pageTitle() {
      return this.test.id ? 'Detalhes do Exame' : 'Novo Exame';
    },
    breadcrumbs() {
      const currentItemText = this.test.id ? 'Detalhes do Exame' : 'Novo Exame';
      return [
        { text: 'Homepage', disabled: false, to: '/home', exact: true },
        { text: 'Exames', disabled: false, to: '/exames', exact: true },
        { text: currentItemText, disabled: true, exact: true },
      ];
    }
  },

  async created() {
    this.loadingPage = true;
    const testId = this.$route.params.id;

    await Promise.all([
      this.loadPatients(),
      this.loadLaboratories(),
      this.loadTestTypes(),
    ]);

    if (testId) {
      await this.loadTest(testId);
    }
    this.loadingPage = false;
  },

  methods: {
    async loadPatients() {
      try {
        const response = await axios.get("/patients", { params: { start: 0, limit: 100 } });
        this.patients = response.data.data || response.data;
      } catch (error) {
        this.$dialog.notify.error("Erro ao carregar lista de pacientes.", { position: "top-right", timeout: 5000 });
      }
    },

    async loadLaboratories() {
      try {
        const response = await axios.get("/laboratories", { params: { start: 0, limit: 100 } });
        this.laboratories = response.data.data || response.data;
      } catch (error) {
        this.$dialog.notify.error("Erro ao carregar lista de laboratórios.", { position: "top-right", timeout: 5000 });
      }
    },

    async loadTestTypes() {
      try {
        const response = await axios.get('/exam-types', { params: { start: 0, limit: 100 } });
        this.testTypes = response.data.data || response.data;
      } catch (error) {
        this.$dialog.notify.error('Erro ao carregar lista de tipos de exame.', { position: 'top-right', timeout: 5000 });
      }
    },

    async loadTest(id) {
      try {
        const response = await axios.get(`/tests/${id}`);
        const data = response.data.data;
        this.test = {
          id: data.id,
          patientId: data.patient ? Number(data.patient.id) : null,
          laboratoryId: data.laboratory ? Number(data.laboratory.id) : null,
          orientation: data.orientation,
          doctorName: data.doctorName,
          testTypeId: data.type ? Number(data.type.id) : null,
        };
      } catch (error) {
        this.$dialog.notify.error('Erro ao carregar detalhes do exame.', { position: 'top-right', timeout: 5000 });
      }
    },

    async saveTest() {
      if (this.$refs.testForm.validate()) {
        this.loadingSave = true;
        try {
          const payload = {
            patient: this.test.patientId,
            laboratoryId: this.test.laboratoryId,
            orientation: this.test.orientation,
            doctorName: this.test.doctorName,
            type: this.test.testTypeId,
          };

          if (this.test.id) {
            await axios.patch(`/tests/${this.test.id}`, { ...payload, operation: "update" });
            this.$dialog.notify.success('Exame atualizado com sucesso!', { position: 'top-right', timeout: 3000 });
          } else {
            const response = await axios.post('/tests', payload);
            this.$dialog.notify.success('Exame criado com sucesso!', { position: 'top-right', timeout: 3000 });
            this.$router.replace(`/exames/${response.data.data.id}`);
          }
        } catch (error) {
          this.$dialog.notify.error('Erro ao salvar exame. Verifique os dados.', { position: 'top-right', timeout: 5000 });
        } finally {
          this.loadingSave = false;
        }
      }
    },
  },
};
</script>
